import Box from '@mui/material/Box';
import { Button } from '@openx/components-core/lib/Button/Button';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  type FieldArrayPath,
  type FieldArrayPathValue,
  type FieldArrayWithId,
  type FieldPath,
  type FieldValues,
  type PathValue,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_FREQUENCY_CAPPING_ENTRY,
  type getTimePeriodOptions as defaultGetTimePeriodOptions,
  MAX_FREQUENCY_CAPPING_ENTRIES,
} from './constants';
import {
  DEFAULT_FREQUENCY_CAPPING_DATA_TEST_PREFIX,
  defaultGetDeleteButtonTestId,
  FrequencyCappingFields,
} from './FrequencyCappingFields';
import { DEFAULT_FREQUENCY_CAPPING_SWITCH_DATA_TEST, FrequencyCappingSwitch } from './FrequencyCappingSwitch';

export const DEFAULT_FREQUENCY_CAPPING_ADD_BUTTON_TEST_ID = 'add-frequency-cap-button';

/** Element type of a field array — RHF's FieldArray type name collides with the FieldArray component. */
type FieldArrayItem<TFieldValues extends FieldValues, TName extends FieldArrayPath<TFieldValues>> =
  FieldArrayPathValue<TFieldValues, TName> extends ReadonlyArray<infer U> | null | undefined ? U : never;

export type FrequencyCappingSectionProps<TFieldValues extends FieldValues> = {
  name: FieldArrayPath<TFieldValues>;
  defaultEntry?: FieldArrayItem<TFieldValues, FieldArrayPath<TFieldValues>>;
  maxEntries?: number;
  infoAlertItems?: ReactNode[];
  dataTestPrefix?: string;
  switchDataTestId?: string;
  addButtonTestId?: string;
  getDeleteButtonTestId?: (index: number) => string;
  getTimePeriodOptions?: typeof defaultGetTimePeriodOptions;
  isDisabled?: boolean;
  disabledInfo?: ReactNode;
};

export const FrequencyCappingSection = <TFieldValues extends FieldValues>({
  name,
  defaultEntry,
  maxEntries = MAX_FREQUENCY_CAPPING_ENTRIES,
  infoAlertItems,
  dataTestPrefix = DEFAULT_FREQUENCY_CAPPING_DATA_TEST_PREFIX,
  switchDataTestId = DEFAULT_FREQUENCY_CAPPING_SWITCH_DATA_TEST,
  addButtonTestId = DEFAULT_FREQUENCY_CAPPING_ADD_BUTTON_TEST_ID,
  getDeleteButtonTestId = defaultGetDeleteButtonTestId,
  getTimePeriodOptions,
  isDisabled = false,
  disabledInfo,
}: FrequencyCappingSectionProps<TFieldValues>) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { isSubmitting },
    getValues,
  } = useFormContext<TFieldValues>();
  const { append, fields, remove, replace } = useFieldArray<TFieldValues>({
    control,
    name,
  });
  const initialValue = getValues(name as unknown as FieldPath<TFieldValues>) as unknown as unknown[] | undefined;
  const [isEnabled, setIsEnabled] = useState(!isDisabled && !!initialValue?.length);
  const canAddMore = fields.length < maxEntries;
  const entryToAppend = (defaultEntry ?? DEFAULT_FREQUENCY_CAPPING_ENTRY) as FieldArrayItem<
    TFieldValues,
    FieldArrayPath<TFieldValues>
  >;

  const handleAdd = () => append(entryToAppend);

  useEffect(() => {
    if (isDisabled && isEnabled) {
      setIsEnabled(false);
    }
  }, [isDisabled, isEnabled]);

  useEffect(() => {
    if (isEnabled && fields.length === 0) {
      replace([entryToAppend]);
    } else if (!isEnabled) {
      replace([] as unknown as PathValue<TFieldValues, FieldArrayPath<TFieldValues>>);
    }
  }, [entryToAppend, fields.length, isEnabled, replace]);

  return (
    <FormSection
      divider
      title={t('Frequency Capping')}
      description={t(
        'Frequency capping is a mechanism to limit how many times the same user is exposed to the same advertiser creative. \n E. g.:  [ 10 ] Impressions per [ 3 ] [ Weeks ]'
      )}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <FrequencyCappingSwitch
        isEnabled={isEnabled}
        isSubmitting={isSubmitting}
        setIsEnabled={setIsEnabled}
        infoAlertItems={infoAlertItems}
        dataTestId={switchDataTestId}
        isDisabled={isDisabled}
        disabledInfo={disabledInfo}
      />

      {isEnabled && (
        <>
          <FrequencyCappingFields<TFieldValues>
            name={name}
            fields={fields as FieldArrayWithId<TFieldValues, FieldArrayPath<TFieldValues>, 'id'>[]}
            onRemove={remove}
            dataTestPrefix={dataTestPrefix}
            getDeleteButtonTestId={getDeleteButtonTestId}
            getTimePeriodOptions={getTimePeriodOptions}
          />
          <Box sx={{ pt: 1 }}>
            <Button
              color="primary"
              variant="text"
              data-test={addButtonTestId}
              disabled={!canAddMore}
              onClick={handleAdd}
            >
              {t('Add another frequency CAP')}
            </Button>
          </Box>
        </>
      )}
    </FormSection>
  );
};
